import { useEffect, useRef, useState } from 'react';
import IconButton from '~/components/IconButton/IconButton';
import RenamingInput from '~/components/editor/RenamingInput/RenamingInput';
import NumberInput from '~/components/widgets/NumberInput/NumberInput';
import { useExperiment } from '~/systems/experiment';
import { assert, findClosestValidAtomPosition, Vec2 } from '~/util';
import type { Atom } from '~/models/studio';
import type { ExperimentTx } from '~/systems/experiment';
import './AtomProperties.css';

interface AtomPropertiesProps {
  atomUuid: string;
}

export default function AtomProperties({ atomUuid }: AtomPropertiesProps) {
  const { atoms, commitedAtoms, device, openTransaction } = useExperiment();

  const atom = atoms.find((a) => a.uuid === atomUuid);

  const txRef = useRef<ExperimentTx | null>(null);
  const cmdIdxRef = useRef(0);

  const [renaming, setRenaming] = useState(false);
  const [hover, setHover] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  function getEditTx() {
    if (txRef.current == null || !txRef.current.isOpen()) {
      txRef.current = openTransaction('atomEdit');
      const thisAtom = atom;
      assert(thisAtom != null);
      cmdIdxRef.current = txRef.current.push({
        kind: 'update_atom',
        uuid: thisAtom.uuid,
        props: {}
      });
    }
    return txRef.current;
  }

  function updateAtom(props: Partial<Omit<Atom, 'uuid'>>) {
    getEditTx().update(cmdIdxRef.current, (cmd) => {
      assert(cmd.kind === 'update_atom');
      const a = commitedAtoms.find((a) => a.uuid === cmd.uuid);
      if (a == null) {
        return cmd;
      }
      return { ...cmd, props: { ...cmd.props, ...props } };
    });
  }

  function updateAtomPosition(idealPos: Vec2) {
    if (!device) {
      return;
    }
    const maxDistance = device.maxRadialDistance;
    const maxDistanceSq = maxDistance * maxDistance;

    // limit the point by the max radial distance of the device
    const normSq = Vec2.normSquare(idealPos);
    if (normSq > maxDistanceSq) {
      const correction = maxDistance / Math.sqrt(normSq);
      idealPos.x *= correction;
      idealPos.y *= correction;
    }

    const otherAtoms = commitedAtoms
      .filter((a) => a.uuid !== atomUuid)
      .map((a) => a.pos);

    const newPos = findClosestValidAtomPosition(
      idealPos,
      otherAtoms,
      device.minAtomDistance,
      device.maxRadialDistance
    );

    updateAtom({ pos: newPos });
  }

  function removeAtom() {
    if (!atom) {
      return;
    }
    const tx = openTransaction('removeAtom');
    tx.push({ kind: 'remove_atom', uuid: atom.uuid });
    tx.commit();
  }

  function commit() {
    if (txRef.current != null && txRef.current.isOpen()) {
      txRef.current.commit();
    }
  }

  // Commit on unmount
  useEffect(() => () => commit(), []);

  function handleSaveNewName(newName: string) {
    if (atom) {
      updateAtom({ label: newName });
      commit();
    }
    setRenaming(false);
    setHover(false);
  }

  function handleOpenRenaming() {
    setRenaming(true);
  }

  function handleCancelRenaming() {
    setRenaming(false);
    setHover(false);
    setInputFocused(false);
  }

  if (!atom) {
    return null;
  }

  const maxDistance = device?.maxRadialDistance || 0;

  return (
    <div className={`AtomProperties${inputFocused ? ' AtomInputFocused' : ''}`}>
      {!renaming ? (
        <div
          className="AtomName"
          role="button"
          tabIndex={0}
          onFocus={() => setHover(true)}
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onClick={(e) => {
            e.stopPropagation();
            handleOpenRenaming();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpenRenaming();
            }
          }}
        >
          <h3>{atom.label}</h3>
          {hover && <IconButton icon="edit" label="Edit" size={16} />}
        </div>
      ) : (
        <RenamingInput
          initialName={atom.label ?? ''}
          onSave={handleSaveNewName}
          onCancel={handleCancelRenaming}
        />
      )}
      {!renaming && (
        <div className="AtomProperties-atomInputs">
          <NumberInput
            className="AtomProperties-numberInput"
            value={atom.pos.x}
            min={-maxDistance}
            max={maxDistance}
            step={0.01}
            unit="μm"
            onBlur={() => {
              setInputFocused(false);
              commit();
            }}
            onFocus={() => setInputFocused(true)}
            onChange={(v) => {
              const committedAtom = commitedAtoms.find(
                (a) => a.uuid === atomUuid
              );
              updateAtomPosition({
                x: v,
                y: committedAtom?.pos.y ?? atom.pos.y
              });
            }}
          />
          <NumberInput
            className="AtomProperties-numberInput"
            value={atom.pos.y}
            min={-maxDistance}
            max={maxDistance}
            step={0.01}
            unit="μm"
            onBlur={() => {
              setInputFocused(false);
              commit();
            }}
            onFocus={() => setInputFocused(true)}
            onChange={(v) => {
              const committedAtom = commitedAtoms.find(
                (a) => a.uuid === atomUuid
              );
              updateAtomPosition({
                x: committedAtom?.pos.x ?? atom.pos.x,
                y: v
              });
            }}
          />
        </div>
      )}
      <IconButton
        square
        icon="trash"
        label="remove"
        padding={4}
        className="RemoveAtomButton"
        background="hover"
        onClick={removeAtom}
      />
    </div>
  );
}
